// Lineups.js
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getLineup, setLineup, getPrevLineup } from '../../services/api';
import PitchView from './PitchView';
import LoadingState from 'components/shared/LoadingState';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';

/**
 * Normalized signature of the semantically meaningful lineup + commentary
 * state, used for dirty tracking. Deliberately excludes volatile fields
 * (opta_playerID, team_optaID) that differ between the API bootstrap shape and
 * the shape TeamPitch pushes back up on mount, and sorts rows so key order
 * doesn't matter.
 */
function computeSig(lineupsObj, commentaryObj) {
  const rows = [];
  Object.entries(lineupsObj).forEach(([fixtureOptaID, teamsObj]) => {
    Object.entries(teamsObj).forEach(([teamFplID, teamEntry]) => {
      (teamEntry?.players || []).forEach((p) => {
        rows.push(`${fixtureOptaID}|${teamFplID}|${p.element}|${p.pos_row}|${p.pos_col}`);
      });
    });
  });
  rows.sort();

  const comm = [];
  Object.entries(commentaryObj).forEach(([fixtureOptaID, teamsObj]) => {
    Object.entries(teamsObj).forEach(([teamFplID, text]) => {
      if (text && text.trim()) comm.push(`${fixtureOptaID}|${teamFplID}|${text.trim()}`);
    });
  });
  comm.sort();

  return JSON.stringify([rows, comm]);
}

export default function Lineups({ user, activeLeague }) {
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [data, setData] = useState(null);
  const [allLineups, setAllLineups] = useState({});

  // Commentary state: { [fixtureOptaID]: { [teamFplID]: "text" } }
  const [allCommentary, setAllCommentary] = useState({});

  const [submitting, setSubmitting] = useState(false);
  const [prevLineupLoading, setPrevLineupLoading] = useState(false);

  // Confirm dialogs
  const [confirmPrevOpen, setConfirmPrevOpen] = useState(false);
  const [confirmIncompleteOpen, setConfirmIncompleteOpen] = useState(false);

  // Dirty tracking baseline (set on load + successful submit)
  const [baselineSig, setBaselineSig] = useState(null);

  // 1) Fetch data
  const fetchData = useCallback(() => {
    if (!user || !activeLeague) return;
    setLoadError(null);
    setLoading(true);
    getLineup(user.gw_status.next_gw)
      .then((res) => setData(res))
      .catch((err) => setLoadError(err.message || 'Error fetching lineup'))
      .finally(() => setLoading(false));
  }, [user, activeLeague]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 2) Bootstrap any already selected players + commentary, and capture the
  //    dirty-tracking baseline from the same initial objects.
  useEffect(() => {
    if (!data) return;

    const initialLineups = (data.selectedPlayers || []).reduce((acc, p) => {
      const f = p.fixture_optaID;
      const t = p.team_fplID;
      const to = p.team_optaID;

      if (!acc[f]) acc[f] = {};
      if (!acc[f][t]) {
        acc[f][t] = { teamOptaID: to, players: [] };
      }

      acc[f][t].players.push({
        element: p.element,
        opta_playerID: p.opta_playerID,
        fixture_optaID: p.fixture_optaID,
        fixture_fplID: p.fixture_fplID,
        pos_col: p.pos_col,
        pos_row: p.pos_row,
        // include any display fields your PitchView needs
      });

      return acc;
    }, {});

    const initialCommentary = (data.lineupCommentary || []).reduce((acc, c) => {
      const f = c.fixture_optaID;
      const t = c.team_fplID;
      if (!acc[f]) acc[f] = {};
      acc[f][t] = c.commentary || '';
      return acc;
    }, {});

    setAllLineups(initialLineups);
    setAllCommentary(initialCommentary);
    setBaselineSig(computeSig(initialLineups, initialCommentary));
  }, [data]);

  // Build elementsByTeam
  const elementsByTeam = useMemo(() => {
    if (!data?.elements) return {};
    return data.elements.reduce((map, el) => {
      map[el.team_fplID] = map[el.team_fplID] || [];
      map[el.team_fplID].push(el);
      return map;
    }, {});
  }, [data]);

  // Handler for loading previous GW lineups
  const handleLoadPrevLineups = async () => {
    if (!user?.gw_status?.next_gw || !data) return;

    setPrevLineupLoading(true);
    try {
      const prevGW = user.gw_status.next_gw - 1;
      const response = await getPrevLineup(prevGW);

      if (response?.prevSelectedPlayers) {
        // Group previous players by team
        const prevPlayersByTeam = response.prevSelectedPlayers.reduce((acc, p) => {
          const teamKey = p.team_fplID || p.team_optaID;
          if (!acc[teamKey]) acc[teamKey] = [];
          acc[teamKey].push(p);
          return acc;
        }, {});

        // Build new lineups based on current fixtures and previous player positions
        const newLineups = {};

        data.fixtures.forEach((fixture) => {
          const fixtureOptaID = fixture.fixture_optaID;
          const fixtureFplID = fixture.fixture_fplID;

          // Process both teams in the fixture
          [fixture.team_a, fixture.team_h].forEach((teamFplID) => {
            const prevPlayers = prevPlayersByTeam[teamFplID] || [];

            if (prevPlayers.length > 0) {
              // Find the team's optaID from the current data
              const currentTeam = data.teams.find((t) => t.team_fplID === teamFplID);
              const teamOptaID = currentTeam?.team_optaID || teamFplID;

              if (!newLineups[fixtureOptaID]) newLineups[fixtureOptaID] = {};
              newLineups[fixtureOptaID][teamFplID] = {
                teamOptaID: teamOptaID,
                players: prevPlayers.map((p) => ({
                  element: p.element,
                  opta_playerID: p.opta_playerID || p.element,
                  fixture_optaID: fixtureOptaID,
                  fixture_fplID: fixtureFplID,
                  pos_col: p.pos_col,
                  pos_row: p.pos_row,
                })),
              };
            }
          });
        });

        // Update the lineups state
        setAllLineups(newLineups);
        toast.success(`Loaded GW${prevGW} lineups`);
      }
    } catch (err) {
      console.error('Error loading previous lineups:', err);
      toast.error('Failed to load previous gameweek lineups');
    } finally {
      setPrevLineupLoading(false);
    }
  };

  // Receive lineup changes from each PitchView
  const handleLineupChange = (fixtureOptaID, teamFplID, teamOptaID, players) => {
    setAllLineups((prev) => ({
      ...prev,
      [fixtureOptaID]: {
        ...prev[fixtureOptaID],
        [teamFplID]: { teamOptaID, players },
      },
    }));
  };

  // Receive commentary changes from each PitchView
  const handleCommentaryChange = (fixtureOptaID, teamFplID, teamOptaID, text) => {
    setAllCommentary((prev) => ({
      ...prev,
      [fixtureOptaID]: {
        ...prev[fixtureOptaID],
        [teamFplID]: text,
      },
    }));
  };

  // Flatten to exactly the fields you need
  const selectedPlayers = useMemo(() => {
    return Object.entries(allLineups).flatMap(([fixture_optaID, teamsObj]) =>
      Object.entries(teamsObj).flatMap(([team_fplID, { teamOptaID, players }]) =>
        players.map((p) => ({
          element: p.element,
          opta_playerID: p.opta_playerID ?? p.element,
          team_fplID: team_fplID,
          team_optaID: teamOptaID,
          fixture_optaID: fixture_optaID,
          fixture_fplID: p.fixture_fplID,
          pos_col: p.pos_col,
          pos_row: p.pos_row,
        }))
      )
    );
  }, [allLineups]);

  // Flatten commentary for submission
  const lineupCommentary = useMemo(() => {
    const result = [];
    Object.entries(allCommentary).forEach(([fixture_optaID, teamsObj]) => {
      Object.entries(teamsObj).forEach(([team_fplID, text]) => {
        if (text && text.trim()) {
          // Get team_optaID from allLineups if available
          const teamOptaID = allLineups[fixture_optaID]?.[team_fplID]?.teamOptaID || team_fplID;
          result.push({
            fixture_optaID,
            team_fplID: parseInt(team_fplID, 10),
            team_optaID: teamOptaID,
            commentary: text.trim(),
          });
        }
      });
    });
    return result;
  }, [allCommentary, allLineups]);

  // Dirty tracking
  const currentSig = useMemo(() => computeSig(allLineups, allCommentary), [allLineups, allCommentary]);
  const isDirty = baselineSig !== null && currentSig !== baselineSig;

  // Teams with an incomplete lineup (fewer than 11 players selected)
  const incompleteTeams = useMemo(() => {
    if (!data?.fixtures) return [];
    const result = [];
    data.fixtures.forEach((fx) => {
      [fx.team_a, fx.team_h].forEach((teamFplID) => {
        const count = allLineups[fx.fixture_optaID]?.[teamFplID]?.players?.length ?? 0;
        if (count !== 11) {
          const team = data.teams?.find((t) => t.team_fplID === teamFplID);
          result.push({ name: team?.name || teamFplID, count });
        }
      });
    });
    return result;
  }, [data, allLineups]);

  // Actual submit
  const doSubmit = () => {
    setConfirmIncompleteOpen(false);
    setSubmitting(true);
    setLineup(selectedPlayers, user.gw_status.next_gw, lineupCommentary)
      .then(() => {
        setBaselineSig(currentSig);
        toast.success('Lineups submitted');
      })
      .catch((err) => {
        console.error(err);
        toast.error('Failed to submit lineups');
      })
      .finally(() => setSubmitting(false));
  };

  // Handler for clicking "Submit lineups" — warn first if any pitch is incomplete
  const onSubmitClick = () => {
    if (submitting) return;
    if (incompleteTeams.length > 0) {
      setConfirmIncompleteOpen(true);
      return;
    }
    doSubmit();
  };

  // Handler for clicking "Get GW[N-1] Lineups" — confirm before overwriting work
  const onLoadPrevClick = () => {
    if (selectedPlayers.length > 0) {
      setConfirmPrevOpen(true);
      return;
    }
    handleLoadPrevLineups();
  };

  const prevGW = user?.gw_status?.next_gw ? user.gw_status.next_gw - 1 : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-token-xl font-semibold">
          Gameweek {user?.gw_status?.next_gw} Lineups
        </h2>
        {isDirty ? (
          <Badge variant="warning">Unsaved changes</Badge>
        ) : (
          data && <span className="text-token-base text-muted-foreground">All changes saved</span>
        )}
      </div>

      {/* --- Buttons Row --- */}
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={onSubmitClick} disabled={!isDirty || submitting}>
          {submitting && <Loader2 className="animate-spin" />}
          Submit lineups
        </Button>

        {prevGW > 0 && (
          <Button
            variant="outline"
            onClick={onLoadPrevClick}
            disabled={prevLineupLoading || !data}
          >
            {prevLineupLoading && <Loader2 className="animate-spin" />}
            {`Get GW${prevGW} Lineups`}
          </Button>
        )}
      </div>

      {/* Fixture jump-list */}
      {data?.fixtures?.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {data.fixtures.map((fx) => {
            const home = data.teams?.find((t) => t.team_fplID === fx.team_a);
            const away = data.teams?.find((t) => t.team_fplID === fx.team_h);
            return (
              <button
                key={fx.fixture_optaID}
                type="button"
                onClick={() =>
                  document
                    .getElementById(`fixture-${fx.fixture_optaID}`)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
                className="rounded-full border border-brand-purple1 px-3 py-1 text-token-base text-brand-purple1 hover:bg-brand-purple1/5"
              >
                {home?.short_name || fx.team_a} v {away?.short_name || fx.team_h}
              </button>
            );
          })}
        </div>
      )}

      {loading && <LoadingState message="Loading lineups..." />}

      {loadError && (
        <Alert variant="destructive" className="max-w-xl">
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>Error: {loadError}</span>
            <Button variant="outline" size="sm" onClick={fetchData}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {data && !loading && !data.fixtures?.length && (
        <p className="p-8 text-center text-token-md text-muted-foreground">
          No fixtures found for this gameweek.
        </p>
      )}

      {data && (
        <div className="flex flex-col gap-6">
          {data.fixtures.map((fx) => (
            <div key={fx.fixture_optaID} id={`fixture-${fx.fixture_optaID}`} className="scroll-mt-4">
              <PitchView
                fixture={fx}
                teams={data.teams}
                odds={data.odds}
                elementsByTeam={elementsByTeam}
                initialLineups={allLineups[fx.fixture_optaID] ?? {}}
                onLineupChange={(teamFplID, players) =>
                  handleLineupChange(
                    fx.fixture_optaID,
                    teamFplID,
                    teamFplID, // mirror until you have real Opta ID
                    players
                  )
                }
                commentary={allCommentary[fx.fixture_optaID] ?? {}}
                onCommentaryChange={handleCommentaryChange}
              />
            </div>
          ))}
        </div>
      )}

      {/* Confirm: overwrite current work with previous GW lineups */}
      <Dialog open={confirmPrevOpen} onOpenChange={setConfirmPrevOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Load GW{prevGW} lineups?</DialogTitle>
            <DialogDescription>
              This will replace all current lineup selections with last gameweek&apos;s. Commentary
              is kept.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmPrevOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setConfirmPrevOpen(false);
                handleLoadPrevLineups();
              }}
            >
              Load lineups
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm: submit with incomplete lineups */}
      <Dialog open={confirmIncompleteOpen} onOpenChange={setConfirmIncompleteOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Some lineups are incomplete</DialogTitle>
            <DialogDescription>
              {incompleteTeams.length} team{incompleteTeams.length === 1 ? ' has' : 's have'} fewer
              than 11 players selected:{' '}
              {incompleteTeams.map((t) => `${t.name} (${t.count}/11)`).join(', ')}. Submit anyway?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmIncompleteOpen(false)}>
              Cancel
            </Button>
            <Button onClick={doSubmit}>Submit anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
